import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Table,
    Button,
    Badge,
    Spinner,
    Form,
    FormGroup,
    Label,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import axiosInstance from "../../../helpers/axiosInstance";
import gsap from "gsap";
import { toast } from "react-toastify";

const Return = () => {
    // State variables
    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalReturns, setTotalReturns] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [limit, setLimit] = useState(10);
    const [selectedReturns, setSelectedReturns] = useState([]);
    const [showBulkDelete, setShowBulkDelete] = useState(false);

    // Search and filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");

    // Modal states
    const [modal, setModal] = useState(false);
    const [returnToUpdate, setReturnToUpdate] = useState(null);
    const [updateStatus, setUpdateStatus] = useState("");
    const [updateReason, setUpdateReason] = useState("");
    const [updatePrice, setUpdatePrice] = useState("");

    // Add these new state variables for the Create Return modal
    const [createModal, setCreateModal] = useState(false);
    const [orderId, setOrderId] = useState("");
    const [orderDetails, setOrderDetails] = useState(null);
    const [newReturnReason, setNewReturnReason] = useState("");
    const [newReturnStatus, setNewReturnStatus] = useState("pending");
    const [newReturnPrice, setNewReturnPrice] = useState("");
    const [fetchingOrder, setFetchingOrder] = useState(false);

    const [breadcrumbItems] = useState([
        { title: "Ecommerce", link: "#" },
        { title: "Returns", link: "#" },
    ]);

    useEffect(() => {
        fetchReturns(1);
    }, []);

    const fetchReturns = async (page = 1) => {
        try {
            setLoading(true);

            // Build query parameters
            let queryParams = `page=${page}&limit=${limit}`;
            if (searchTerm) queryParams += `&search=${searchTerm}`;
            if (statusFilter) queryParams += `&status=${statusFilter}`;
            if (startDate) queryParams += `&startDate=${startDate}`;
            if (endDate) queryParams += `&endDate=${endDate}`;
            if (minPrice) queryParams += `&minPrice=${minPrice}`;
            if (maxPrice) queryParams += `&maxPrice=${maxPrice}`;

            const response = await axiosInstance.get(`api/v1/admin/returns?${queryParams}`);

            setReturns(response.data.returns);
            setTotalReturns(response.data.totalReturns);
            setTotalPages(response.data.totalPages);
            setCurrentPage(page);
        } catch (error) {
            console.error("Error fetching returns:", error);
            toast.error("Failed to load returns. Please refresh the page.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (returnId) => {
        if (window.confirm("Are you sure you want to delete this return?")) {
            try {
                setLoading(true);
                await axiosInstance.delete(`api/v1/admin/returns/${returnId}`);
                toast.success("Return deleted successfully");
                setReturns(prevReturns => prevReturns.filter(r => r._id !== returnId));

                // Update total count
                setTotalReturns(prev => prev - 1);
            } catch (error) {
                console.error("Error deleting return:", error);
                toast.error(error.response?.data?.message || "Error deleting return");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleBulkDelete = async () => {
        if (selectedReturns.length === 0) {
            toast.warning("Please select at least one return to delete.");
            return;
        }

        if (window.confirm(`Are you sure you want to delete ${selectedReturns.length} selected returns?`)) {
            try {
                setLoading(true);

                // Delete returns one by one since bulk delete isn't provided in the API
                const deletePromises = selectedReturns.map(id =>
                    axiosInstance.delete(`api/v1/admin/returns/${id}`)
                );

                await Promise.all(deletePromises);

                setReturns(prevReturns =>
                    prevReturns.filter(r => !selectedReturns.includes(r._id))
                );

                toast.success("Returns deleted successfully");
                setSelectedReturns([]);
                setShowBulkDelete(false);
                setTotalReturns(prev => prev - selectedReturns.length);
            } catch (error) {
                console.error("Error deleting returns:", error);
                toast.error(error.response?.data?.message || "Error deleting returns");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleCheckboxChange = (returnId) => {
        let updatedSelectedReturns;
        if (selectedReturns.includes(returnId)) {
            updatedSelectedReturns = selectedReturns.filter(id => id !== returnId);
        } else {
            updatedSelectedReturns = [...selectedReturns, returnId];
        }
        setSelectedReturns(updatedSelectedReturns);
        setShowBulkDelete(updatedSelectedReturns.length > 0);
    };

    const handlePageChange = (page) => {
        fetchReturns(page);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleFilterSubmit = () => {
        setCurrentPage(1);
        fetchReturns(1);
    };

    const clearFilters = () => {
        setSearchTerm("");
        setStatusFilter("");
        setStartDate("");
        setEndDate("");
        setMinPrice("");
        setMaxPrice("");
        setCurrentPage(1);
        fetchReturns(1);
    };

    // Modal toggle function
    const toggleModal = () => {
        setModal(!modal);
        if (!modal) {
            setReturnToUpdate(null);
            setUpdateStatus("");
            setUpdateReason("");
            setUpdatePrice("");
        }
    };

    // Open update modal with pre-filled data
    const openUpdateModal = (returnData) => {
        console.log("Opening modal with return data:", returnData);
        
        // Make sure we have a valid return object
        if (!returnData || !returnData._id) {
            toast.error("Invalid return data");
            return;
        }
        
        setReturnToUpdate(returnData);
        setUpdateStatus(returnData.status || "");
        setUpdateReason(returnData.reason || "");
        setUpdatePrice(returnData.price?.toString() || "");
        setModal(true);
    };

    // Handle return update
    const handleUpdate = async () => {
        if (!returnToUpdate) return;
        
        try {
            setLoading(true);
            
            const updateData = {};
            if (updateStatus) updateData.status = updateStatus;
            if (updateReason) updateData.reason = updateReason;
            if (updatePrice) updateData.price = parseFloat(updatePrice);
            
            // Make sure we have at least one field to update
            if (Object.keys(updateData).length === 0) {
                toast.warning("Please update at least one field");
                setLoading(false);
                return;
            }
            
            console.log("Updating return with data:", updateData);
            
            const response = await axiosInstance.put(
                `api/v1/admin/returns/${returnToUpdate._id}`,
                updateData
            );
            
            console.log("Update response:", response.data);
            
            if (response.data.success) {
                // Make sure we're mapping correctly based on the API response
                const updatedReturn = response.data.return || {};
                
                setReturns(prevReturns => 
                    prevReturns.map(r => r._id === returnToUpdate._id ? updatedReturn : r)
                );
                
                toast.success("Return updated successfully");
                toggleModal();
            } else {
                toast.error(response.data.message || "Update failed");
            }
        } catch (error) {
            console.error("Error updating return:", error);
            toast.error(error.response?.data?.message || "Error updating return");
        } finally {
            setLoading(false);
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    // Format price for display
    const formatPrice = (price) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
        }).format(price || 0);
    };

    // Toggle create return modal
    const toggleCreateModal = () => {
        setCreateModal(!createModal);
        if (!createModal) {
            // Reset form when opening
            setOrderId("");
            setOrderDetails(null);
            setNewReturnReason("");
            setNewReturnStatus("pending");
            setNewReturnPrice("");
        }
    };

    // Fetch order details by ID
    const fetchOrderDetails = async () => {
        if (!orderId) {
            toast.warning("Please enter an order ID");
            return;
        }

        try {
            setFetchingOrder(true);
            const response = await axiosInstance.get(`api/v1/admin/orders/${orderId}`);

            if (response.data.success) {
                setOrderDetails(response.data.order);
                // Pre-fill the price with the order's total amount if available
                if (response.data.order.totalAmount) {
                    setNewReturnPrice(response.data.order.totalAmount);
                }
            } else {
                toast.error("Failed to fetch order details");
            }
        } catch (error) {
            console.error("Error fetching order details:", error);
            toast.error(error.response?.data?.message || "Order not found");
            setOrderDetails(null);
        } finally {
            setFetchingOrder(false);
        }
    };

    // Handle create return submission
    const handleCreateReturn = async () => {
        if (!orderDetails) {
            toast.warning("Please fetch order details first");
            return;
        }

        if (!newReturnReason) {
            toast.warning("Please enter a reason for the return");
            return;
        }

        try {
            setLoading(true);
            const returnData = {
                orderId: orderDetails.id,
                reason: newReturnReason,
                status: newReturnStatus,
                price: parseFloat(newReturnPrice) || 0
            };

            const response = await axiosInstance.post("api/v1/admin/returns", returnData);

            if (response.data.success) {
                toast.success("Return created successfully");
                toggleCreateModal();
                // Refresh returns list
                fetchReturns(1);
            } else {
                toast.error("Failed to create return");
            }
        } catch (error) {
            console.error("Error creating return:", error);
            toast.error(error.response?.data?.message || "Error creating return");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Returns Management" breadcrumbItems={breadcrumbItems} />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <CardTitle className="h4">Returns List</CardTitle>
                                    <div className="d-flex gap-2">
                                        {/* Add Create Return button */}
                                        <Button
                                            color="success"
                                            onClick={toggleCreateModal}
                                        >
                                            <i className="mdi mdi-plus me-1"></i> Create Return
                                        </Button>
                                        {showBulkDelete && (
                                            <Button
                                                color="danger"
                                                onClick={handleBulkDelete}
                                            >
                                                Bulk Delete
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Improved Filters */}
                                <Card className="mb-4 filter-card">
                                    <CardBody>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5 className="mb-0">
                                                <i className="mdi mdi-filter-outline me-1"></i>
                                                Search & Filters
                                            </h5>
                                            <Button
                                                color="link"
                                                className="p-0 text-primary"
                                                onClick={clearFilters}
                                            >
                                                <i className="mdi mdi-refresh me-1"></i> Clear All
                                            </Button>
                                        </div>

                                        <Row>
                                            <Col lg={3} md={6} className="mb-3">
                                                <FormGroup>
                                                    <Label for="searchTerm" className="text-muted">
                                                        <i className="mdi mdi-magnify me-1"></i>Search Returns
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        id="searchTerm"
                                                        placeholder="Search by reason text..."
                                                        value={searchTerm}
                                                        onChange={handleSearch}
                                                        className="rounded-pill"
                                                    />
                                                </FormGroup>
                                            </Col>

                                            <Col lg={3} md={6} className="mb-3">
                                                <FormGroup>
                                                    <Label for="statusFilter" className="text-muted">
                                                        <i className="mdi mdi-checkbox-marked-circle-outline me-1"></i>Return Status
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        id="statusFilter"
                                                        value={statusFilter}
                                                        onChange={(e) => setStatusFilter(e.target.value)}
                                                        className="rounded-pill"
                                                    >
                                                        <option value="">All Statuses</option>
                                                        <option value="pending">Pending</option>
                                                        <option value="approved">Approved</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>

                                            <Col lg={3} md={6} className="mb-3">
                                                <Label className="text-muted">
                                                    <i className="mdi mdi-currency-inr me-1"></i>Price Range
                                                </Label>
                                                <div className="d-flex gap-2">
                                                    <Input
                                                        type="number"
                                                        placeholder="Min Price"
                                                        value={minPrice}
                                                        onChange={(e) => setMinPrice(e.target.value)}
                                                        className="rounded-start"
                                                    />
                                                    <Input
                                                        type="number"
                                                        placeholder="Max Price"
                                                        value={maxPrice}
                                                        onChange={(e) => setMaxPrice(e.target.value)}
                                                        className="rounded-end"
                                                    />
                                                </div>
                                            </Col>

                                            <Col lg={3} md={6} className="mb-3">
                                                <Label className="text-muted">
                                                    <i className="mdi mdi-calendar-range me-1"></i>Return Date
                                                </Label>
                                                <div className="d-flex gap-2">
                                                    <div className="w-50">
                                                        <small className="text-muted d-block mb-1">From</small>
                                                        <Input
                                                            type="date"
                                                            value={startDate}
                                                            onChange={(e) => setStartDate(e.target.value)}
                                                            className="rounded"
                                                        />
                                                    </div>
                                                    <div className="w-50">
                                                        <small className="text-muted d-block mb-1">To</small>
                                                        <Input
                                                            type="date"
                                                            value={endDate}
                                                            onChange={(e) => setEndDate(e.target.value)}
                                                            className="rounded"
                                                        />
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>

                                        <div className="d-flex justify-content-end mt-1">
                                            <Button
                                                color="primary"
                                                onClick={handleFilterSubmit}
                                                className="px-4 rounded-pill"
                                            >
                                                <i className="mdi mdi-filter me-1"></i> Apply Filters
                                            </Button>
                                        </div>
                                    </CardBody>
                                </Card>

                                {/* Active Filters Display */}
                                {(searchTerm || statusFilter || startDate || endDate || minPrice || maxPrice) && (
                                    <div className="mb-4 p-3 bg-light rounded">
                                        <div className="d-flex align-items-center mb-2">
                                            <i className="mdi mdi-filter text-primary me-2"></i>
                                            <h6 className="mb-0">Active Filters:</h6>
                                            <Button
                                                color="link"
                                                size="sm"
                                                className="ms-auto p-0"
                                                onClick={clearFilters}
                                            >
                                                Clear All
                                            </Button>
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {searchTerm && (
                                                <Badge color="info" className="p-2">
                                                    Search: {searchTerm}
                                                    <i
                                                        className="mdi mdi-close-circle ms-1"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setSearchTerm("");
                                                            handleFilterSubmit();
                                                        }}
                                                    ></i>
                                                </Badge>
                                            )}
                                            {statusFilter && (
                                                <Badge color="primary" className="p-2">
                                                    Status: {statusFilter}
                                                    <i
                                                        className="mdi mdi-close-circle ms-1"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setStatusFilter("");
                                                            handleFilterSubmit();
                                                        }}
                                                    ></i>
                                                </Badge>
                                            )}
                                            {(minPrice || maxPrice) && (
                                                <Badge color="success" className="p-2">
                                                    Price: {minPrice || '0'} - {maxPrice || '∞'}
                                                    <i
                                                        className="mdi mdi-close-circle ms-1"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setMinPrice("");
                                                            setMaxPrice("");
                                                            handleFilterSubmit();
                                                        }}
                                                    ></i>
                                                </Badge>
                                            )}
                                            {(startDate || endDate) && (
                                                <Badge color="warning" className="p-2">
                                                    Date: {startDate || 'Any'} to {endDate || 'Any'}
                                                    <i
                                                        className="mdi mdi-close-circle ms-1"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setStartDate("");
                                                            setEndDate("");
                                                            handleFilterSubmit();
                                                        }}
                                                    ></i>
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="table-responsive">
                                    <Table className="table-centered table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedReturns.length === returns.length && returns.length > 0}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedReturns(returns.map((r) => r._id));
                                                                setShowBulkDelete(true);
                                                            } else {
                                                                setSelectedReturns([]);
                                                                setShowBulkDelete(false);
                                                            }
                                                        }}
                                                    />
                                                </th>
                                                <th>Order ID</th>
                                                <th>Reason</th>
                                                <th>Status</th>
                                                <th>Price</th>
                                                <th>Created Date</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {returns.map((returnItem, index) => (
                                                <tr
                                                    key={returnItem._id}
                                                    className="return-row"
                                                    style={{
                                                        opacity: 0,
                                                        transform: "translateY(20px)",
                                                    }}
                                                    ref={(el) => {
                                                        if (el) {
                                                            gsap.to(el, {
                                                                opacity: 1,
                                                                y: 0,
                                                                duration: 0.1,
                                                                delay: index * 0.01,
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedReturns.includes(returnItem._id)}
                                                            onChange={() => handleCheckboxChange(returnItem._id)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Link
                                                            to={`/order-details/${returnItem.orderId}`}
                                                            target="_blank"
                                                            className="fw-bold text-primary"
                                                        >
                                                            {returnItem.orderId}
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        <div style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                            {returnItem.reason || "No reason provided"}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge
                                                            color={
                                                                returnItem.status === "approved" ? "success" :
                                                                    returnItem.status === "pending" ? "warning" :
                                                                        "danger"
                                                            }
                                                            className="px-2"
                                                        >
                                                            {returnItem.status || "pending"}
                                                        </Badge>
                                                    </td>
                                                    <td>{formatPrice(returnItem.price)}</td>
                                                    <td>{formatDate(returnItem.createdAt)}</td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                color="info"
                                                                size="sm"
                                                                onClick={() => openUpdateModal(returnItem)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                onClick={() => handleDelete(returnItem._id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>

                                    {returns.length === 0 && !loading && (
                                        <div className="text-center my-5">
                                            <h4>No returns found</h4>
                                        </div>
                                    )}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="d-flex justify-content-center mt-4">
                                        <nav>
                                            <ul className="pagination">
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                    >
                                                        Previous
                                                    </button>
                                                </li>

                                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                                    <li
                                                        key={page}
                                                        className={`page-item ${currentPage === page ? 'active' : ''}`}
                                                    >
                                                        <button
                                                            className="page-link"
                                                            onClick={() => handlePageChange(page)}
                                                        >
                                                            {page}
                                                        </button>
                                                    </li>
                                                ))}

                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        Next
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {loading && (
                    <div className="text-center my-4">
                        <Spinner color="primary" />
                    </div>
                )}

                {/* Create Return Modal */}
                <Modal isOpen={createModal} toggle={toggleCreateModal} size="lg">
                    <ModalHeader toggle={toggleCreateModal}>
                        Create New Return
                    </ModalHeader>
                    <ModalBody>
                        <Form>
                            <Row className="align-items-end mb-3">
                                <Col md={8}>
                                    <FormGroup>
                                        <Label for="orderId" className="text-muted">
                                            <i className="mdi mdi-package-variant me-1"></i>Order ID
                                        </Label>
                                        <Input
                                            type="text"
                                            id="orderId"
                                            placeholder="Enter order ID"
                                            value={orderId}
                                            onChange={(e) => setOrderId(e.target.value)}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <Button
                                        color="primary"
                                        onClick={fetchOrderDetails}
                                        disabled={fetchingOrder || !orderId}
                                        className="w-100"
                                    >
                                        {fetchingOrder ? (
                                            <><Spinner size="sm" /> Fetching...</>
                                        ) : (
                                            <>Fetch Order Details</>
                                        )}
                                    </Button>
                                </Col>
                            </Row>

                            {orderDetails && (
                                <>
                                    <div className="order-details-box p-3 bg-light rounded mb-3">
                                        <h5 className="border-bottom pb-2 mb-3">Order Information</h5>
                                        <Row>
                                            <Col md={6}>
                                                <p className="mb-1">
                                                    <strong>Order ID:</strong> {orderDetails.id}
                                                </p>
                                                <p className="mb-1">
                                                    <strong>Order Date:</strong> {formatDate(orderDetails.createdAt)}
                                                </p>
                                                <p className="mb-1">
                                                    <strong>Total Amount:</strong> {formatPrice(orderDetails.totalAmount)}
                                                </p>
                                            </Col>
                                            <Col md={6}>
                                                <p className="mb-1">
                                                    <strong>Payment Status:</strong>
                                                    <Badge
                                                        color={orderDetails.paymentStatus === "completed" ? "success" : "warning"}
                                                        className="ms-2"
                                                    >
                                                        {orderDetails.paymentStatus}
                                                    </Badge>
                                                </p>
                                                <p className="mb-1">
                                                    <strong>Order Status:</strong>
                                                    <Badge
                                                        color={
                                                            orderDetails.status === "delivered" ? "success" :
                                                                orderDetails.status === "shipped" ? "info" :
                                                                    "warning"
                                                        }
                                                        className="ms-2"
                                                    >
                                                        {orderDetails.status==="outForDelivery"?"Dispatched":orderDetails.status}
                                                    </Badge>
                                                </p>
                                            </Col>
                                        </Row>

                                        {orderDetails.shippingAddress && (
                                            <div className="mt-3">
                                                <h6>Shipping Address:</h6>
                                                <p className="mb-0 small">
                                                    {orderDetails.shippingAddress.name}, {orderDetails.shippingAddress.address1},
                                                    {orderDetails.shippingAddress.city}, {orderDetails.shippingAddress.state} {orderDetails.shippingAddress.pinCode}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <h5 className="mb-3">Return Details</h5>
                                    <FormGroup>
                                        <Label for="newReturnReason">Return Reason*</Label>
                                        <Input
                                            type="textarea"
                                            id="newReturnReason"
                                            placeholder="Enter reason for return"
                                            value={newReturnReason}
                                            onChange={(e) => setNewReturnReason(e.target.value)}
                                            rows="3"
                                            required
                                        />
                                    </FormGroup>

                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="newReturnStatus">Status</Label>
                                                <Input
                                                    type="select"
                                                    id="newReturnStatus"
                                                    value={newReturnStatus}
                                                    onChange={(e) => setNewReturnStatus(e.target.value)}
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="approved">Approved</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="newReturnPrice">Refund Amount</Label>
                                                <Input
                                                    type="number"
                                                    id="newReturnPrice"
                                                    placeholder="Enter return amount"
                                                    value={newReturnPrice}
                                                    onChange={(e) => setNewReturnPrice(e.target.value)}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggleCreateModal}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onClick={handleCreateReturn}
                            disabled={!orderDetails || !newReturnReason || loading}
                        >
                            {loading ? <><Spinner size="sm" /> Processing...</> : "Create Return"}
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Update Return Modal - Remove the unnecessary conditional rendering */}
                <Modal isOpen={modal} toggle={toggleModal}>
                    <ModalHeader toggle={toggleModal}>
                        Update Return
                    </ModalHeader>
                    <ModalBody>
                        <Form>
                            <div className="bg-light p-3 mb-3 rounded">
                                {console.log("returnToUpdate",returnToUpdate)}
                                <h6 className="mb-2">Return ID: <small className="text-muted">{returnToUpdate?._id}</small></h6>
                                {returnToUpdate?.orderId && (
                                    <h6 className="mb-2">
                                        Order ID: <Link to={`/order-details/${typeof returnToUpdate.orderId === 'object' ? returnToUpdate.orderId._id : returnToUpdate.orderId}`} 
                                                 target="_blank" className="text-primary">
                                            {returnToUpdate.orderId}
                                        </Link>
                                    </h6>
                                )}
                                <h6 className="mb-0">Created: <small className="text-muted">{returnToUpdate?.createdAt ? formatDate(returnToUpdate.createdAt) : ''}</small></h6>
                            </div>
                            
                            <FormGroup>
                                <Label for="updateStatus">Status*</Label>
                                <Input
                                    type="select"
                                    id="updateStatus"
                                    value={updateStatus}
                                    onChange={(e) => setUpdateStatus(e.target.value)}
                                    className="form-select"
                                    required
                                >
                                    <option value="">Select Status</option>
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                </Input>
                            </FormGroup>
                            
                            <FormGroup>
                                <Label for="updateReason">Reason*</Label>
                                <Input
                                    type="textarea"
                                    id="updateReason"
                                    value={updateReason}
                                    onChange={(e) => setUpdateReason(e.target.value)}
                                    rows="3"
                                    required
                                />
                            </FormGroup>
                            
                            <FormGroup>
                                <Label for="updatePrice">Price*</Label>
                                <Input
                                    type="number"
                                    id="updatePrice"
                                    value={updatePrice}
                                    onChange={(e) => setUpdatePrice(e.target.value)}
                                    required
                                />
                                <small className="text-muted">Enter the refund amount</small>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggleModal}>
                            Cancel
                        </Button>
                        <Button 
                            color="primary" 
                            onClick={handleUpdate}
                            disabled={!updateStatus || !updateReason || loading}
                        >
                            {loading ? <><Spinner size="sm" /> Updating...</> : "Update Return"}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default Return;
